import useAuthStore from '../stores/useAuthStore';

const AUTH_STORAGE_KEY = 'auth-storage';
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

const stripAuthTokensFromUrl = () => {
    try {
        const url = new URL(window.location.href);

        // Remove token-like params from query (including refreshToken)
        ['token', 'access_token', 'accessToken', 'authorization', 'refreshToken', 'refresh_token'].forEach((k) => {
            url.searchParams.delete(k);
        });

        // Remove token-like params from hash fragment (treat as querystring)
        const rawHash = (url.hash || '').startsWith('#') ? url.hash.slice(1) : (url.hash || '');
        if (rawHash) {
            const hashParams = new URLSearchParams(rawHash);
            ['token', 'access_token', 'accessToken', 'authorization', 'refreshToken', 'refresh_token'].forEach((k) => {
                hashParams.delete(k);
            });
            const nextHash = hashParams.toString();
            url.hash = nextHash ? `#${nextHash}` : '';
        }

        // Apply without navigation
        window.history.replaceState({}, document.title, url.toString());
    } catch {
        // no-op
    }
};

const normalizeBearer = (token) => {
    if (!token) return null;
    return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
};

const readPersistedAuthToken = () => {
    try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);

        // zustand persist default shape: { state: {...}, version: number }
        const token = parsed?.state?.token ?? null;
        return token || null;
    } catch {
        return null;
    }
};

export const getAccessToken = () => {
    const direct = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (direct) return normalizeBearer(direct);

    // Try in-memory store first (might already be hydrated)
    const storeToken = useAuthStore.getState?.().token;
    if (storeToken) {
        const normalized = normalizeBearer(storeToken);
        localStorage.setItem(ACCESS_TOKEN_KEY, normalized);
        return normalized;
    }

    // Fallback: read persisted zustand storage directly (avoids hydration timing issues)
    const persisted = readPersistedAuthToken();
    if (persisted) {
        const normalized = normalizeBearer(persisted);
        localStorage.setItem(ACCESS_TOKEN_KEY, normalized);
        return normalized;
    }

    return null;
};

export const setAccessToken = (token, username = 'Social User', refreshToken = null) => {
    const normalized = normalizeBearer(token);
    if (!normalized) return;
    localStorage.setItem(ACCESS_TOKEN_KEY, normalized);

    // Refresh Token도 함께 저장 (있는 경우)
    if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }

    useAuthStore.getState().login(normalized, username);

    // Avoid re-processing the same token on refresh/back
    stripAuthTokensFromUrl();
};

// Refresh Token 관련 함수들
export const getRefreshToken = () => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setRefreshToken = (refreshToken) => {
    if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
};

export const clearAccessToken = () => {
    // 1) Clear explicit token cache
    localStorage.removeItem(ACCESS_TOKEN_KEY);

    // 1-1) Clear refresh token as well
    localStorage.removeItem(REFRESH_TOKEN_KEY);

    // 2) Clear any session-scoped token used by websocket, etc.
    try {
        sessionStorage.removeItem('token');
    } catch {
        // no-op
    }

    // 3) Clear persisted zustand auth so getAccessToken() can't resurrect an old token
    try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
        // no-op
    }

    // 4) Clear in-memory store state
    useAuthStore.getState().logout();

    // Avoid immediate re-login via leftover URL token params
    stripAuthTokensFromUrl();
};
